// ─────────────────────────────────────────────────────────────────────────────
// REQ-125 — Tablero ejecutivo de KPIs (/app/tablero).
//
// Consume GET v1/analytics/recognition/summary?includeInactivity=true (RecognitionKpiDto[]): K-01..
// K-10 + K-11 (% nunca entraron) / K-12 (% entraron sin actividad). `suprimido=true` (umbral de
// anonimato Europa <5) → se pinta "—", nunca se inventa el dato.
//
// D4 (2026-09-30, decisión del cliente) — los KPIs siguen SIN puntos, pero debajo va un bloque
// «Puntos de campaña» SOLO para administradores de puntos (`puedeVerPuntos`): el total de puntos
// otorgados por una campaña con «Aplican puntos», con la OU y el rango de la barra. El Tablero no es
// por campaña, así que la campaña se elige dentro del propio bloque.
// Estados honestos (loading/error/empty). Gateo UX por rol (RoleGuard); la autoridad es el backend.
//
// RF-143 (C4) — DRILL-DOWN. El requisito pide «KPIs … del scope del usuario con drill-down por OU,
// generación y Fundamental», y añade «sin mockup de UX definido: se reutiliza la vista de analítica
// de PMP». Así que aquí no manda la maqueta: se reutiliza el mismo `FilterBar` de Reportería, y los
// dos selectores propios se ponen a continuación con el mismo estilo de barra.
//
// Las tres dimensiones viajan ya al backend. Las dos nuevas cambian CÓMO se calculan los KPIs: sin
// filtro se lee el agregado (rápido); con filtro se van a los recibos de proyección, porque el
// agregado guarda personas y dimensiones pero nunca las CRUZA.
//
// 🔴 El Fundamental NO se toma del `fundFilter` de la barra compartida. Ese selector ofrece
// COMPORTAMIENTOS cuando el Fundamental los tiene, y ese id no existe en `fundamentalIds` de un
// reconocimiento: el tablero saldría en cero sin que nada fallara. Aquí se usa el catálogo único de
// Fundamentales (`useRecognitionCatalog`), que es la misma fuente de la que sale el id al reconocer.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useRecognitionSummary, useRecognitionCatalog, getFundamentalName, useCampaigns, useCampaignPoints,
  getCampaignName,
} from '@/api/recognition';
import { RoleGuard, useEffectiveRoles } from '@/auth/RoleGuard';
import { puedeVerPuntos, campanaConPuntos } from '../analytics/puntosDeCampana';
import { FilterBar } from '@/views/AnalyticsView/components/FilterBar';
import { DEFAULT_FILTERS } from '@/views/AnalyticsView/hooks/useAnalyticsFilters';
import '@/views/AnalyticsView/AnalyticsView.css';
import type { RecognitionKpiDto } from '@/api/recognition';
import { RecognitionShell, RecognitionHeader, StateBlock } from '../components';
import './metrics.css';

/**
 * Bandas generacionales, EXACTAMENTE como las calcula `GenerationHelper` de UserAdminService y las
 * persiste en `AppUser.Generation`. Los valores no se traducen —son la llave del filtro—; solo su
 * rótulo. Inventar una etiqueta aquí devolvería un tablero en cero sin error ninguno.
 */
const GENERACIONES = ['GenAlpha', 'GenZ', 'Millennial', 'GenX', 'BabyBoomer', 'Silent'] as const;

/**
 * Formatea el valor de un KPI. Los dos huecos posibles se pintan distinto A PROPÓSITO:
 *   · `suprimido` → «—». El dato EXISTE y se tapa por anonimato (<5 personas).
 *   · `noAplicaConFiltro` → «n/a». El dato NO EXISTE con ese filtro: el censo no se desglosa por
 *     generación, así que la tasa que dividiera por él significaría otra cosa.
 * Usar el mismo símbolo para ambos diría que hay un dato oculto donde no hay dato.
 */
function formatKpi(k: RecognitionKpiDto): string {
  if (k.noAplicaConFiltro) return 'n/a';
  if (k.suprimido) return '—';
  if (k.unidad === 'percent') return `${Number.isInteger(k.valor) ? k.valor : k.valor.toFixed(1)}%`;
  if (k.unidad === 'ratio') return k.valor.toFixed(2);
  // count u otros → entero legible.
  return Math.round(k.valor).toLocaleString();
}

/** Acento de color por bloque de KPIs (paleta cultural de Sigma). */
const KPI_COLORS = ['#342F82', '#F08521', '#20B15A', '#40B8E9', '#E11F26', '#EF5395'];

/**
 * D4 (2026-09-30) — bloque «Puntos de campaña» del Tablero. Solo lo monta quien administra puntos
 * (así no se pide la lista de campañas ni el total a quien no los puede ver). Lista solo las campañas
 * con «Aplican puntos»: elegir una sin puntos daría un cero que se leería como «no se otorgó nada».
 */
function PuntosDeCampana({ ou, desde, hasta }: Readonly<{ ou?: string; desde?: string; hasta?: string }>) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language.split('-')[0];
  const campanas = useCampaigns(undefined, 1, 100);
  const conPuntos = (campanas.data?.items ?? []).filter(campanaConPuntos);
  const [campaignId, setCampaignId] = useState('');
  const elegida = conPuntos.find((c) => c.id === campaignId) ?? conPuntos[0] ?? null;
  const puntos = useCampaignPoints(
    elegida?.id ?? '',
    { from: desde, to: hasta, ouIds: ou ? [ou] : undefined },
    Boolean(elegida),
  );

  let valor = '—';
  if (puntos.data) valor = puntos.data.puntos.toLocaleString(i18n.language);

  return (
    <section className="met-puntos" data-testid="tablero-puntos" aria-label={t('tablero.pointsTitle')}>
      <div className="met-puntos-head">
        <h3 className="met-puntos-title">
          <i className="fas fa-coins" aria-hidden="true" /> {t('tablero.pointsTitle')}
        </h3>
        {conPuntos.length > 0 && (
          <select
            className="au-cascade-select"
            value={elegida?.id ?? ''}
            onChange={(e) => setCampaignId(e.target.value)}
            aria-label={t('tablero.pointsCampaign')}
          >
            {conPuntos.map((c) => (
              <option key={c.id} value={c.id}>{getCampaignName(c, lang)}</option>
            ))}
          </select>
        )}
      </div>
      {conPuntos.length === 0 ? (
        <p className="met-puntos-empty">
          {campanas.isLoading ? t('reports.common.loading') : t('tablero.pointsNone')}
        </p>
      ) : (
        <div className="met-kpi-grid">
          <div className="met-kpi">
            <div className="met-kpi-key met-puntos-key">{t('reports.shine.pointsAwarded')}</div>
            <div className="met-kpi-value">{puntos.isError ? '—' : valor}</div>
            <div className="met-kpi-name">
              {puntos.data
                ? t('reports.shine.pointsAwardedHint', {
                    nominaciones: puntos.data.nominaciones, personas: puntos.data.personas,
                  })
                : t('tablero.pointsHint')}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// FASE 3 — exportado para que AdminView.tsx lo embeba como tab ("Tablero"), ya que /app/tablero
// quedó sin punto de entrada al eliminar la nav horizontal (RecognitionNav). Ver CONTRATO-RUTAS.md §7.
export function TableroContent() {
  const { t, i18n } = useTranslation('recognition');

  // RF-143 — el drill-down. Solo las dimensiones que el backend sabe aplicar (ver cabecera).
  // Arranca SIN rango, no con el de `DEFAULT_FILTERS`. Ese trae fechas precargadas, y adoptarlas
  // convertiría el tablero de «todo el histórico» a «un período» sin que nadie lo pidiera: los mismos
  // KPIs pasarían a significar otra cosa el día del despliegue. El drill-down se añade; lo que el
  // tablero contaba hasta hoy no se toca hasta que alguien elija un rango.
  const SIN_RANGO = { ...DEFAULT_FILTERS, dateFrom: '', dateTo: '' };
  const [filters, setFilters] = useState(SIN_RANGO);
  const setFilter = <K extends keyof typeof DEFAULT_FILTERS>(key: K, value: (typeof DEFAULT_FILTERS)[K]) =>
    setFilters(prev => ({ ...prev, [key]: value }));

  // Las dos dimensiones nuevas viven aparte de `filters`: no son campos de `AnalyticsFilters` y
  // colarlas ahí obligaría a tocar la barra compartida de Reportería, que no las usa.
  const [fundamentalId, setFundamentalId] = useState('');
  const [generacion, setGeneracion] = useState('');

  const resetFilters = () => {
    setFilters({ ...DEFAULT_FILTERS, dateFrom: '', dateTo: '' });
    setFundamentalId('');
    setGeneracion('');
  };

  const catalogo = useRecognitionCatalog();
  const fundamentales = (catalogo.data ?? []).filter((f) => f.isActive);

  const params = useMemo(
    () => ({
      includeInactivity: true, // REQ-125 → añade K-11/K-12.
      scope: filters.ou || undefined,
      from: filters.dateFrom || undefined,
      to: filters.dateTo || undefined,
      fundamentalId: fundamentalId || undefined,
      generacion: generacion || undefined,
    }),
    [filters.ou, filters.dateFrom, filters.dateTo, fundamentalId, generacion],
  );

  const q = useRecognitionSummary(params);
  const verPuntos = puedeVerPuntos(useEffectiveRoles());
  const kpis = q.data ?? [];
  const isEmpty = q.data != null && kpis.length === 0;
  const suppressed = kpis.some((k) => k.suprimido);
  const noAplica = kpis.some((k) => k.noAplicaConFiltro);

  return (
    <>
      <RecognitionHeader title={t('tablero.title')} subtitle={t('tablero.subtitle')} />

      {/*
        * RF-143 — la barra va FUERA del StateBlock: si viviera dentro, un error de la consulta se
        * llevaría por delante los filtros y quien acaba de acotar por OU se quedaría sin poder
        * cambiarlos ni volver atrás.
        */}
      <FilterBar filters={filters} setFilter={setFilter} resetFilters={resetFilters} />

      {/*
        * RF-143 — las dos dimensiones propias del reconocimiento. Van en su propia barra y no dentro
        * de `FilterBar`: esa barra la comparten todas las pestañas de analítica de Pulso, y ninguna
        * de ellas sabe filtrar por estas dos.
        */}
      <div className="au-filter-bar met-drill-bar">
        <select
          className="au-cascade-select"
          value={fundamentalId}
          onChange={(e) => setFundamentalId(e.target.value)}
          aria-label={t('tablero.allFundamentals')}
          disabled={fundamentales.length === 0}
        >
          <option value="">{t('tablero.allFundamentals')}</option>
          {fundamentales.map((f) => (
            <option key={f.id} value={f.id}>{getFundamentalName(f, i18n.language)}</option>
          ))}
        </select>

        <div className="au-filter-bar-sep" />

        <select
          className="au-cascade-select"
          value={generacion}
          onChange={(e) => setGeneracion(e.target.value)}
          aria-label={t('tablero.allGenerations')}
        >
          <option value="">{t('tablero.allGenerations')}</option>
          {GENERACIONES.map((g) => (
            <option key={g} value={g}>{t(`tablero.generations.${g}`)}</option>
          ))}
        </select>
      </div>

      <StateBlock
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        isEmpty={isEmpty}
        emptyText={t('tablero.empty')}
        onRetry={() => q.refetch()}
      >
        <div className="met-wrap">
          {suppressed && (
            <div className="rr-suppressed-note">
              <i className="fas fa-user-shield" aria-hidden="true" /> {t('reports.common.suppressedNote')}
            </div>
          )}

          {/* Aviso propio, y no el de anonimato: son dos huecos con causas distintas y quien lee el
              tablero tiene que poder distinguirlos. */}
          {noAplica && (
            <div className="rr-suppressed-note">
              <i className="fas fa-info-circle" aria-hidden="true" /> {t('tablero.notApplicableNote')}
            </div>
          )}

          <div className="met-kpi-grid">
            {kpis.map((k, i) => (
              <div
                key={k.key}
                className={`met-kpi ${k.suprimido || k.noAplicaConFiltro ? 'met-kpi--suppressed' : ''}`}
                title={k.noAplicaConFiltro ? t('tablero.notApplicableNote') : undefined}
              >
                <div className="met-kpi-key" style={{ color: KPI_COLORS[i % KPI_COLORS.length] }}>{k.key}</div>
                <div className="met-kpi-value">{formatKpi(k)}</div>
                <div className="met-kpi-name">{k.nombre}</div>
                {k.scopeId && <div className="met-kpi-scope">{k.scopeId}</div>}
              </div>
            ))}
          </div>
        </div>
      </StateBlock>

      {verPuntos && (
        <PuntosDeCampana
          ou={filters.ou || undefined}
          desde={filters.dateFrom || undefined}
          hasta={filters.dateTo || undefined}
        />
      )}
    </>
  );
}

export function TableroView() {
  const { t } = useTranslation('recognition');
  return (
    <RecognitionShell>
      <RoleGuard
        allow={['AdminOU', 'AdminGlobal']}
        // La capacidad `TableroMetricasLideres` está declarada con NoRoleRestriction: el
        // backend autoriza por permiso `viewAnalytics` + alcance Team, sin mirar el rol. Sin esta
        // línea, un jefe no-admin pasaba el servidor y lo bloqueaba la pantalla.
        orPermission="viewAnalytics"
        fallback={<div className="adm-denied" role="alert"><i className="fas fa-lock" /> {t('admin.denied')}</div>}
      >
        <TableroContent />
      </RoleGuard>
    </RecognitionShell>
  );
}

export default TableroView;
